import firebase from 'firebase';
import { introwise } from 'common/types';

type Timestamp = firebase.firestore.Timestamp;

const convertSession = ({
  dateFromLocal,
  dateToLocal,
  ...rest
}: introwise.Agenda.Session<Timestamp>): introwise.Agenda.Session<Date> => ({
  ...rest,
  dateFromLocal: dateFromLocal.toDate(),
  dateToLocal: dateToLocal.toDate(),
});

const convertTrack = ({
  dateFromLocal,
  sessions,
  ...rest
}: introwise.Agenda.Track<Timestamp>): introwise.Agenda.Track<Date> => ({
  ...rest,
  dateFromLocal: dateFromLocal.toDate(),
  sessions: sessions.map(convertSession),
});

const convertDay = ({ dateFromLocal, tracks }: introwise.Agenda.Day<Timestamp>): introwise.Agenda.Day<Date> => ({
  dateFromLocal: dateFromLocal.toDate(),
  tracks: tracks.map(convertTrack),
});

const convertSchedule = (schedule: introwise.Agenda.Schedule<Timestamp>): introwise.Agenda.Schedule<Date> =>
  schedule.map(convertDay);

const convertMatchmakingTimeAvailability = (
  matchmakingTimeAvailability: introwise.Event<Timestamp>['matchmakingTimeAvailability'],
): introwise.Event['matchmakingTimeAvailability'] =>
  matchmakingTimeAvailability.map(availability => ({
    dateFromLocal: availability.dateFromLocal.toDate(),
    dateToLocal: availability.dateToLocal.toDate(),
  }));

export const convertFirestoreEvent = (event: introwise.Event<Timestamp>): introwise.Event => {
  const { dateTo, dateFrom, dateToLocal, dateFromLocal, schedule, matchmakingTimeAvailability, ...rest } = event;
  return {
    dateTo: dateTo && dateTo.toDate(),
    dateFrom: dateFrom && dateFrom.toDate(),
    dateToLocal: dateToLocal && dateToLocal.toDate(),
    dateFromLocal: dateFromLocal && dateFromLocal.toDate(),
    ...(!!schedule && { schedule: convertSchedule(schedule) }),
    ...(!!matchmakingTimeAvailability && {
      matchmakingTimeAvailability: convertMatchmakingTimeAvailability(matchmakingTimeAvailability),
    }),
    ...rest,
  };
};

export const convertFirestoreCircle = (circle: introwise.Circle<Timestamp>): introwise.Circle => {
  const { createdAt, dateFromLocal, dateToLocal, ...rest } = circle;
  return {
    createdAt: createdAt && createdAt.toDate(),
    dateFromLocal: dateFromLocal && dateFromLocal.toDate(),
    dateToLocal: dateToLocal && dateToLocal.toDate(),
    ...rest,
  };
};

export const convertFirestoreMeeting = (circle: introwise.Meeting<Timestamp>): introwise.Meeting => {
  const { createdAt, dateFromLocal, dateToLocal, dateFrom, dateTo, ...rest } = circle;
  return {
    createdAt: createdAt && createdAt.toDate(),
    dateFromLocal: dateFromLocal && dateFromLocal.toDate(),
    dateToLocal: dateToLocal && dateToLocal.toDate(),
    dateFrom: dateFrom && dateFrom.toDate(),
    dateTo: dateTo && dateTo.toDate(),
    ...rest,
  };
};

export const convertFirestoreUser = (user: introwise.User<Timestamp>): introwise.User => {
  const { createdAt, ...rest } = user;
  return {
    createdAt: createdAt && createdAt.toDate(),
    ...rest,
  };
};

export const convertFirestoreEventParticipant = (
  participant: introwise.EventParticipant<Timestamp>,
): introwise.EventParticipant => {
  const { joinedAt, ...rest } = participant;
  return {
    joinedAt: joinedAt && joinedAt.toDate(),
    ...rest,
  };
};

export const convertFirestoreEventParticipantEdge = (
  participant: introwise.EventParticipantEdge<Timestamp>,
): introwise.EventParticipantEdge => {
  const { joinedAt, meeting, ...rest } = participant;
  return {
    joinedAt: joinedAt && joinedAt.toDate(),
    ...(meeting && {
      meeting: {
        ...meeting,
        createdAt: meeting.createdAt && meeting.createdAt.toDate(),
        dateFromLocal: meeting.dateFromLocal && meeting.dateFromLocal.toDate(),
      },
    }),
    ...rest,
  };
};

export const convertFirestoreMessage = (message: introwise.Message<Timestamp>): introwise.Message => {
  const { timestamp, ...rest } = message;
  return {
    timestamp: timestamp && timestamp.toDate(),
    ...rest,
  };
};

export const convertFirestoreCommunity = (community: introwise.Community<Timestamp>): introwise.Community => {
  const { createdAt, ...rest } = community;
  return {
    createdAt: createdAt && createdAt.toDate(),
    ...rest,
  };
};

export const convertFirestoreCommunityMembershipRequest = (
  request: introwise.CommunityMembershipRequest<Timestamp>,
): introwise.CommunityMembershipRequest => {
  const { sentAt, ...rest } = request;
  return {
    sentAt: sentAt && sentAt.toDate(),
    ...rest,
  };
};

export const convertFirestorePoll = (poll: introwise.Poll<Timestamp>): introwise.Poll => {
  const { createdAt, votingClosedAt, ...rest } = poll;
  return {
    createdAt: createdAt && createdAt.toDate(),
    votingClosedAt: votingClosedAt && votingClosedAt.toDate(),
    ...rest,
  };
};

export const convertFirestorePollVote = (pollVote: introwise.PollResponse<Timestamp>): introwise.PollResponse => {
  const { respondedAt: votedAt, ...rest } = pollVote;
  return {
    respondedAt: votedAt && votedAt.toDate(),
    ...rest,
  };
};
